import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInventory } from "../../inventory/InventoryContext";
import { usePlans } from "../../plans/PlansContext";
import { dayLabel, formatDateTime, formatTime, timeAgo } from "../../inventory/time";
import Icon from "../../components/ui/Icon";
import Modal from "../../components/ui/Modal";
import "../inventory/InventoryPage.css";
import "./RecentPage.css";

// The change history, newest first and grouped by day: channels, media
// plans and accounts.
// ?channel=<id> narrows it to one channel (the edit dialog links here).

const KINDS = [
  { key: "all", label: "All" },
  { key: "added", label: "Added", actions: ["add", "duplicate"] },
  { key: "edited", label: "Edited", actions: ["edit"] },
  { key: "deleted", label: "Deleted", actions: ["delete"] },
  { key: "imported", label: "Imported", actions: ["import"] },
  { key: "other", label: "Undo and reset", actions: ["undo", "reset"] },
  { key: "plans", label: "Media plans", actions: ["plan_add", "plan_edit", "plan_delete"] },
  { key: "users", label: "Users", actions: ["user_add", "user_edit", "user_delete"] },
];

const TONE = {
  add: "green",
  duplicate: "green",
  edit: "blue",
  delete: "red",
  import: "yellow",
  undo: "gray",
  reset: "gray",
  plan_add: "green",
  plan_edit: "blue",
  plan_delete: "red",
  user_add: "green",
  user_edit: "blue",
  user_delete: "red",
};

function PlanName({ plan, exists }) {
  const name = <strong>{plan?.name || "a plan"}</strong>;
  return exists ? (
    <Link to={`/plans/${encodeURIComponent(plan.id)}`} className="rc-channel">
      {name}
    </Link>
  ) : (
    <span className="rc-channel is-gone">{name}</span>
  );
}

const PAGE = 100;

function ChannelName({ ref_, exists }) {
  const text = (
    <>
      <strong>{ref_.channelName}</strong>
      {ref_.partner && <span className="rc-partner"> ({ref_.partner})</span>}
    </>
  );
  // Still in the inventory: link to it there
  return exists ? (
    <Link to={`/inventory?q=${encodeURIComponent(ref_.channelName)}`} className="rc-channel">
      {text}
    </Link>
  ) : (
    <span className="rc-channel is-gone">{text}</span>
  );
}

function Summary({ entry, exists, planExists }) {
  const first = entry.channels?.[0];
  const many = (entry.channels?.length || 0) > 1;
  switch (entry.action) {
    case "add":
      return <>added {first && <ChannelName ref_={first} exists={exists(first.id)} />}</>;
    case "duplicate":
      return <>added a copy: {first && <ChannelName ref_={first} exists={exists(first.id)} />}</>;
    case "edit":
      return <>edited {first && <ChannelName ref_={first} exists={exists(first.id)} />}</>;
    case "delete":
      return many ? (
        <>deleted {entry.channels.length} channels</>
      ) : (
        <>deleted {first && <ChannelName ref_={first} exists={false} />}</>
      );
    case "import": {
      const { added = 0, updated = 0 } = entry.counts || {};
      const parts = [added && `${added} added`, updated && `${updated} updated`].filter(Boolean);
      return (
        <>
          imported {entry.note ? <strong>{entry.note}</strong> : "a file"}
          {parts.length > 0 && <span className="rc-muted"> · {parts.join(", ")}</span>}
        </>
      );
    }
    case "reset":
      return <>restored the sample data</>;
    case "undo":
      return <>undid {entry.note ? <span className="rc-muted">“{entry.note}”</span> : "a change"}</>;
    case "plan_add":
      return <>created the media plan <PlanName plan={entry.plan} exists={planExists(entry.plan?.id)} /></>;
    case "plan_edit":
      return <>edited the media plan <PlanName plan={entry.plan} exists={planExists(entry.plan?.id)} /></>;
    case "plan_delete":
      return <>deleted the media plan <PlanName plan={entry.plan} exists={false} /></>;
    case "user_add":
      return <>added the user <strong>{entry.target?.email}</strong></>;
    case "user_edit":
      return <>edited the user <strong>{entry.target?.email}</strong></>;
    case "user_delete":
      return <>deleted the user <strong>{entry.target?.email}</strong></>;
    default:
      return <>{entry.action}</>;
  }
}

function Entry({ entry, exists, planExists }) {
  const [open, setOpen] = useState(false);
  const list = entry.channels || [];
  const hasList = (entry.action === "delete" || entry.action === "import") && list.length > 1;
  const hasChanges = entry.changes?.length > 0;

  return (
    <li className={`rc-entry tone-${TONE[entry.action] || "gray"}`}>
      <span className="rc-dot" aria-hidden="true" />
      <div className="rc-main">
        <div className="rc-line">
          <span className="rc-text">
            <span className="rc-user">{entry.user}</span> <Summary entry={entry} exists={exists} planExists={planExists} />
            {entry.action?.startsWith("plan_") && entry.note && <span className="rc-muted"> ({entry.note})</span>}
          </span>
          <time className="rc-time" dateTime={new Date(entry.at).toISOString()} title={formatDateTime(entry.at)}>
            {formatTime(entry.at)}
            <span className="rc-ago"> · {timeAgo(entry.at)}</span>
          </time>
        </div>

        {hasChanges && (
          <ul className="rc-changes">
            {entry.changes.map((c) => (
              <li key={c.key}>
                <span className="rc-field">{c.label}</span>
                <span className="rc-from">{c.from || "empty"}</span>
                <Icon name="chevron" size={12} className="rot-270" />
                <span className="rc-to">{c.to || "empty"}</span>
              </li>
            ))}
          </ul>
        )}

        {hasList && (
          <>
            <button type="button" className="btn-link rc-toggle" onClick={() => setOpen((o) => !o)}>
              {open ? "Hide channels" : `Show ${list.length} channels`}
            </button>
            {open && (
              <ul className="rc-names">
                {list.map((c) => (
                  <li key={c.id}>
                    <ChannelName ref_={c} exists={entry.action !== "delete" && exists(c.id)} />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </li>
  );
}

export default function RecentPage() {
  const { activity, channels, clearActivity } = useInventory();
  const { plans } = usePlans();
  const planIds = useMemo(() => new Set(plans.map((p) => p.id)), [plans]);
  const planExists = (id) => planIds.has(id);
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState("all");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [confirmClear, setConfirmClear] = useState(false);

  const channelId = params.get("channel");
  const ids = useMemo(() => new Set(channels.map((c) => c.id)), [channels]);
  const exists = (id) => ids.has(id);

  // The channel being followed, by its latest name in the log
  const followed = channelId
    ? channels.find((c) => c.id === channelId) ||
      activity.flatMap((e) => e.channels || []).find((c) => c.id === channelId)
    : null;

  const filtered = useMemo(() => {
    const actions = KINDS.find((k) => k.key === kind)?.actions;
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return activity.filter((e) => {
      if (actions && !actions.includes(e.action)) return false;
      if (channelId && !(e.channels || []).some((c) => c.id === channelId)) return false;
      if (words.length) {
        const text = [
          e.user,
          e.note,
          e.plan?.name,
          e.target?.email,
          ...(e.channels || []).flatMap((c) => [c.channelName, c.partner]),
          ...(e.changes || []).flatMap((c) => [c.label, c.from, c.to]),
        ]
          .join(" ")
          .toLowerCase();
        if (!words.every((w) => text.includes(w))) return false;
      }
      return true;
    });
  }, [activity, kind, query, channelId]);

  // Group the visible entries by day
  const groups = [];
  filtered.slice(0, shown).forEach((e) => {
    const label = dayLabel(e.at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.entries.push(e);
    else groups.push({ label, entries: [e] });
  });

  const counts = useMemo(() => {
    const c = {};
    KINDS.forEach((k) => {
      c[k.key] = k.actions ? activity.filter((e) => k.actions.includes(e.action)).length : activity.length;
    });
    return c;
  }, [activity]);

  return (
    <div className="rc">
      <div className="inv-head">
        <div>
          <h1>Recent changes</h1>
          <p>
            Every change to the inventory in this browser, newest first. Draft: kept locally until there's a
            database.
          </p>
        </div>
        <div className="inv-actions">
          <button type="button" className="btn" disabled={!activity.length} onClick={() => setConfirmClear(true)}>
            <Icon name="trash" /> Clear history
          </button>
        </div>
      </div>

      <div className="rc-tools">
        <div className="search-box">
          <Icon name="search" />
          <input
            type="search"
            placeholder="Search by channel, partner, person or value…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShown(PAGE);
            }}
            aria-label="Search history"
          />
        </div>
        <div className="rc-kinds" role="tablist" aria-label="Kind of change">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              role="tab"
              aria-selected={kind === k.key}
              className={`filter-btn${kind === k.key ? " is-active" : ""}`}
              onClick={() => {
                setKind(k.key);
                setShown(PAGE);
              }}
            >
              {k.label}
              <span className="rc-count">{counts[k.key]}</span>
            </button>
          ))}
        </div>
      </div>

      {followed && (
        <div className="filter-chips">
          <span className="filter-chip">
            Channel: {followed.channelName}
            <button
              type="button"
              aria-label="Show all channels"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("channel");
                setParams(next, { replace: true });
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        </div>
      )}

      <div className="inv-card rc-card">
        {activity.length === 0 ? (
          <div className="inv-empty">
            <h2>No changes yet</h2>
            <p>Adding, editing, deleting or importing channels will show up here.</p>
            <div className="inv-empty-actions">
              <Link to="/inventory" className="btn">
                Go to the inventory
              </Link>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="inv-empty">
            <h2>Nothing matches</h2>
            <p>Try another kind of change or a different search.</p>
          </div>
        ) : (
          <>
            {groups.map((g) => (
              <section key={g.label} className="rc-day">
                <h2 className="rc-day-label">{g.label}</h2>
                <ul className="rc-list">
                  {g.entries.map((e) => (
                    <Entry key={e.id} entry={e} exists={exists} planExists={planExists} />
                  ))}
                </ul>
              </section>
            ))}
            {filtered.length > shown && (
              <div className="rc-more">
                <button type="button" className="btn" onClick={() => setShown((n) => n + PAGE)}>
                  Show more ({filtered.length - shown} left)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {confirmClear && (
        <Modal
          title="Clear history?"
          onClose={() => setConfirmClear(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmClear(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() => {
                  clearActivity();
                  setConfirmClear(false);
                }}
              >
                Clear {activity.length} entries
              </button>
            </>
          }
        >
          <p className="modal-text">
            This removes the change history from this browser. The channels themselves stay as they are. It
            can't be undone.
          </p>
        </Modal>
      )}
    </div>
  );
}
